/**
 * AgentInbox — single agent chatbox (admin-only).
 * Renders incoming queue, current status, sent history.
 */
import React from 'react';

const AGENT_COLORS = {
  nova:         '#22d3ee',
  orion:        '#f59e0b',
  vega:         '#22c55e',
  atlas:        '#ef4444',
  producer:     '#60a5fa',
  quill:        '#d4a574',
  scribe:       '#a78bfa',
  delta:        '#94a3b8',
};

const STATUS_DOT = {
  idle: '#94a3b8',
  thinking: '#facc15',
  waiting: '#60a5fa',
  blocked: '#ef4444',
  sent: '#4de2c0',
};

function fmt(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toISOString().slice(11, 19) + 'Z'; } catch { return iso; }
}

export default function AgentInbox({ inbox, highlighted = false }) {
  const color = AGENT_COLORS[inbox.agent] || '#dbe3f2';
  const lastIn = (inbox.incoming || []).slice(-1)[0];
  const lastSent = (inbox.sent || []).slice(-1)[0];

  return (
    <div
      data-testid={`agent-inbox-${inbox.agent}`}
      style={{
        background: 'rgba(10,14,22,0.7)',
        border: `1px solid ${highlighted ? color : 'rgba(120,150,200,0.18)'}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 6,
        padding: '10px 12px',
        minHeight: 100,
        fontSize: 12,
        boxShadow: highlighted ? `0 0 0 2px ${color}33` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color, fontWeight: 600, letterSpacing: '0.2em', fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>
          {inbox.agent.toUpperCase()}
        </span>
        <span
          data-testid={`agent-status-${inbox.agent}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 9, letterSpacing: '0.2em', color: STATUS_DOT[inbox.status] || '#94a3b8',
          }}
        >
          <span style={{
            width: 7, height: 7, borderRadius: 4,
            background: STATUS_DOT[inbox.status] || '#94a3b8',
            animation: inbox.status === 'thinking' ? 'pulse 1.2s infinite' : 'none',
          }} />
          {(inbox.status || 'idle').toUpperCase()}
        </span>
      </div>
      {inbox.current_task && (
        <div style={{ color: 'rgba(200,210,230,0.6)', fontSize: 10, marginBottom: 6 }}>
          · {inbox.current_task}
        </div>
      )}
      {lastIn ? (
        <div style={rowIn}>
          <div style={rowLabel}>← from {lastIn.from_agent}</div>
          <div style={rowText}>{lastIn.text.slice(0, 140)}{lastIn.text.length > 140 ? '…' : ''}</div>
        </div>
      ) : (
        <div style={rowEmpty}>nothing in-queue</div>
      )}
      {lastSent && (
        <div style={rowOut}>
          <div style={{ ...rowLabel, color: '#4de2c0' }}>→ sent {fmt(lastSent.created_at)} (conf {Number(lastSent.confidence).toFixed(2)})</div>
          <div style={rowText}>{lastSent.text.slice(0, 140)}{lastSent.text.length > 140 ? '…' : ''}</div>
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}</style>
    </div>
  );
}

const rowIn = { background: 'rgba(96,165,250,0.08)', padding: '6px 8px', borderRadius: 4, marginBottom: 6 };
const rowOut = { background: 'rgba(77,226,192,0.08)', padding: '6px 8px', borderRadius: 4 };
const rowLabel = { fontSize: 9, letterSpacing: '0.14em', color: 'rgba(200,210,230,0.55)', marginBottom: 3 };
const rowText = { fontSize: 11, lineHeight: 1.45, color: '#e5ebf3', fontFamily: 'Georgia, serif' };
const rowEmpty = { fontSize: 10, color: 'rgba(200,210,230,0.35)', fontStyle: 'italic', padding: '6px 8px' };
